import React, { useEffect, useRef, useState } from 'react';
import { UserRound, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { checkPhotoSize } from '../../lib/uploadLimits';
import { parseApiError } from '../../lib/apiErrors';

const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

// Shared photo avatar + upload control for the self and admin profile pages.
// Fetches the photo via an authed blob request (no static mount) and re-fetches
// after a successful upload so a replace is reflected immediately.
export function StudentPhotoUpload({ fetchPhoto, uploadPhoto, disabled }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [hasPhoto, setHasPhoto] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const blobUrlRef = useRef(null);

  const load = () => {
    fetchPhoto()
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = url;
        setPhotoUrl(url);
        setHasPhoto(true);
      })
      .catch(() => setHasPhoto(false));
  };

  useEffect(() => {
    load();
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed');
      return;
    }
    const sizeError = checkPhotoSize(file);
    if (sizeError) {
      toast.error(sizeError);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    try {
      await uploadPhoto(formData);
      toast.success('Photo updated');
      load();
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to upload photo').message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary/10 text-primary dark:bg-accent-cool/10 dark:text-accent-cool">
        {hasPhoto && photoUrl ? (
          <img src={photoUrl} alt="Student" className="h-full w-full object-cover" />
        ) : (
          <UserRound size={26} />
        )}
      </div>
      <div>
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
        >
          <Upload size={16} />
          {uploading ? 'Uploading…' : 'Upload Photo'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          className="hidden"
          onChange={handleFileChange}
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">JPG or PNG, up to 2MB</p>
      </div>
    </div>
  );
}
